import { CloudProcess } from "./CloudProcess";
import { CloudProgress } from "./CloudProgress";
import { CloudProjectFileTransfer } from "./CloudProjectFileTransfer";
import { DeviceCloudMetadata } from "./DeviceCloudMetadata";
import { EmptyResponse, CloudResponse, SuccesfulResponse } from "./CloudResponse";
import { CloudFontFileTransfer } from "./CloudFontFileTransfer";
import { CloudUrl } from "./CloudUrl";
import { BtDiskFileUtils } from "../../shared/sync/dataTypes/BtDiskFileUtils";
import { ProcessType } from './ProcessType';
import { FoldersManager } from './FoldersManager';
import { BtDraftMigrator } from '../../shared/sync/dataTypes/BtDraftMigrator';
import { BtUtils } from '@/shared/sync/dataTypes/BtUtils';
import { WaitingList } from '@/service-worker/sync/WaitingList';
import { AtomicDictionary } from '@/shared/sync/dataTypes/AtomicDictionary';
import { ThrottleManager } from '@/shared/sync/dataTypes/ThrottleManager';
import { FirebaseWrapper } from '@/service-worker/firebase/FirebaseBaseWrapper';
import { SyncDecoder } from './SyncDecoder';
import { CloudTempProject } from '@/shared/sync/dataTypes/CloudTempProject';
import { DraftSyncVersion } from '@/shared/sync/dataTypes/DraftSyncVersion';
import { FolderObject } from '@/shared/sync/dataTypes/FolderObject';
import { Project } from '@/shared/sync/dataTypes/Project';
import { ProjectFileMetaData } from '@/shared/sync/dataTypes/ProjectFileMetaData';
import { SyncStatus } from '@/shared/sync/dataTypes/SyncStatus';
import { UserDevice } from '@/shared/sync/dataTypes/UserDevice';
import { CloudUploadTempDataManager } from '@/shared/sync/dataTypes/CloudUploadTempDataManager';
import { CloudManagerBase } from "./CloudManagerBase";
import { rebuildProjectFilesIndex } from "@/firebase/RebuildFilesIndex";
import * as Sentry from "@sentry/react";

const minDraftVersion = 1

export interface CloudManagerDelegate {
    cloudManagerDidUploadMetaData(): void;
    cloudManagerDidDetectMetaDataChange(): void;
    cloudManagerDidReceivedCloudFolders(folders: FolderObject[]): void;
    cloudManagerDidReceivedRecycleBinItems(items: object): void;
    cloudManagerDidStartUploadProject?(callbackId: string): void;
    cloudManagerDidDeleteProject?(projectId: string, callbackId: string): void;
}

export default class CloudManager extends CloudManagerBase {
    delegate?: CloudManagerDelegate;
    
    static get shared(): CloudManager {
        let globalScope = globalThis as unknown as ServiceWorkerGlobalScope;
        // @ts-ignore
        if (!globalScope.cloudManager) {
            console.error('called CloudManager.shared');
            // @ts-ignore
            globalScope.cloudManager = new CloudManager();
        }
        // @ts-ignore
        return globalScope.cloudManager;
    }
    
    async setup(){
        await CloudUploadTempDataManager.shared.setup();
        return { userId: this.userId, isLoggedIn: this.isLoggedIn };
    }

    private fetchingByDraftId = new AtomicDictionary<string, { process: CloudProcess, progress?: CloudProgress }>();
    private uploadingByDraftId = new AtomicDictionary<string, { process: CloudProcess, progress?: CloudProgress }>();
    private metadataThrottle = new ThrottleManager<EmptyResponse>();
    private foldersThrottle = new ThrottleManager<EmptyResponse>();
    private uploadList = new WaitingList<ProjectFileMetaData[] | void>(Infinity, "sync: upload project", false);
    private cachedProjectFilesList: Map<string, ProjectFileMetaData[]> = new Map();
    
    private static readonly MAX_CACHED_PROJECTS = 10;


    ////////////////////////
    ///////// api //////////
    ////////////////////////
    //MARK: api

    async downloadCloudMetadata(): Promise<CloudResponse<DeviceCloudMetadata[]> | EmptyResponse> {
        // await this.updateFonts();

        this.logWithTimestamp("sync: fetching devices list");
        const cloudDevicesResponse = await this.downloadDeviceIds();
        if (cloudDevicesResponse.error) {
            return cloudDevicesResponse as EmptyResponse;
        }
        let devices = (cloudDevicesResponse as CloudResponse<string[]>).value

        this.logWithTimestamp(`sync: fetching ${devices.length} devices metadata`);
        const response = await Promise.all(devices.map(deviceId => this.downloadMetaData(deviceId)));
        this.logWithTimestamp("sync: did complete fetching devices metadata");

        const errors = response.filter(res => res.error);
        if (errors.length > 0) {
            console.error(`sync: ${errors.map(e => e.error).join(", ")})}`)
        }
        return new SuccesfulResponse(response.flatMap(res => res.value).filter(value => !!value)) as CloudResponse<DeviceCloudMetadata[]>;
    }

    async downloadCloudUnsafeFeed(): Promise<CloudResponse<DraftSyncVersion[]> | EmptyResponse> {
        try {
            const data = await CloudUrl.get(CloudUrl.unsafeFeedMetaData());
            let decoder = new SyncDecoder<DraftSyncVersion>();
            const metadata = decoder.convertArray(data);
            return new SuccesfulResponse(metadata)
        } catch (error) {
            return EmptyResponse.sendError(`sync: couldn't fetch metadata of feed list. error: ${error}`);
        }
    }

    async uploadFolders(): Promise<EmptyResponse> {
        const folders = FoldersManager.shared.folders;
        return await this.foldersThrottle.throttle(0.1, async () => {
            try {
                await CloudUrl.putJson(CloudUrl.foldersMetaData(), folders);
                this.logWithTimestamp("sync: did upload folders to cloud");
                return EmptyResponse.success();
            } catch (error) {
                return EmptyResponse.sendError(`sync: failed to upload folders. error: ${error}`);
            }
        });
    }

    async fetchFoldersMetaDataArray(): Promise<CloudResponse<FolderObject[]>> {
        try {
            const data = await CloudUrl.get(CloudUrl.foldersMetaData());
            let decoder = new SyncDecoder<FolderObject>();
            const folders = decoder.convertArray(data);
            this.logWithTimestamp("sync: did received cloud folders");
            return new SuccesfulResponse(folders);
        } catch (error) {
            EmptyResponse.sendError(`sync: couldn't fetch folders. error: ${error}`);
        }
    }

    async uploadProjectsMetaDataArray(projects: DraftSyncVersion[], unsafeFeed?: DraftSyncVersion[]): Promise<EmptyResponse> {
        this.logWithTimestamp("sync: throttle uploading metadata of device");
        const res = await this.metadataThrottle.throttle(100, async () => {
            return this.throttledUploadProjectsMetaDataArray(projects, unsafeFeed);
        });
        return res;
    }

    private async throttledUploadProjectsMetaDataArray(projects: DraftSyncVersion[], unsafeFeed?: DraftSyncVersion[]): Promise<EmptyResponse> {
        const device = UserDevice.current;

        this.logWithTimestamp(`sync: uploading ${projects.length} projects metadata of device: ${device.id}`);

        const metadata = new DeviceCloudMetadata(device, projects);

        try {
            await CloudUrl.putJson(CloudUrl.deviceMetaData(device.id), metadata);
        } catch (error) {
            return EmptyResponse.sendError(`sync: failed to upload metadata of device: ${metadata.device.id}. error: ${error}`);
        }

        if (unsafeFeed) {
            try {
                let unsafeFeedWithPrefix = [];
                for (let feedItem of unsafeFeed) {
                    unsafeFeedWithPrefix.push({ 'bazaart.DraftSyncVersion': feedItem })
                }
                await CloudUrl.putJson(CloudUrl.unsafeFeedMetaData(), unsafeFeedWithPrefix);
            } catch (error) {
                this.logWithTimestamp(`sync: failed to upload unsafe feed. error: ${error}`);
            }
        }

        this.delegate?.cloudManagerDidUploadMetaData();
        return EmptyResponse.success();
    }

    async cancelFetchingProject(projectId: string) {
        const action = this.fetchingByDraftId.get(projectId);
        if (!action) return;
        if (action.progress) action.progress.isCanceled = true;
        await action.process.cancel();
        this.fetchingByDraftId.delete(projectId);
    }

    async cancelUploadingProject(projectId: string) {
        const action = this.uploadingByDraftId.get(projectId);
        if (!action) return;
        if (action.progress) action.progress.isCanceled = true;
        await action.process.cancel();
        this.uploadingByDraftId.delete(projectId);
    }

    async logout() {
        super.logout();
        await CloudUploadTempDataManager.shared.clear();
        this.clearCache();
    }
    
    /**
     * Clears the cached project files list to free memory
     */
    clearCache() {
        this.cachedProjectFilesList.clear();
    }
    
    /**
     * Trims the cache to prevent unbounded growth
     */
    private trimCache() {
        if (this.cachedProjectFilesList.size > CloudManager.MAX_CACHED_PROJECTS) {
            const keysToRemove = Array.from(this.cachedProjectFilesList.keys())
                .slice(0, this.cachedProjectFilesList.size - CloudManager.MAX_CACHED_PROJECTS);
            keysToRemove.forEach(key => this.cachedProjectFilesList.delete(key));
        }
    }
    
    async cancelAllUploads() {
        this.logWithTimestamp("sync: cancel all uploads");
        for (const projectId of this.uploadingByDraftId.keys()) {
            await this.cancelUploadingProject(projectId);
        }
    }

    async fetchProject(projectId: string, device: UserDevice, refetch?: CloudTempProject, latestOnly: boolean = false): Promise<CloudResponse<CloudTempProject> | EmptyResponse> {
        await this.cancelFetchingProject(projectId);

        const process = new CloudProcess(projectId, ProcessType.Download);
        this.fetchingByDraftId.set(projectId, { process });

        const response = await this.fetchProjectUpdatedVersion(projectId, device);

        const clearDownloading = () => {
            this.fetchingByDraftId.delete(projectId)
        }

        if (response.error) {
            clearDownloading()
            return EmptyResponse.sendError(response.error);
        }
        
        const version = response.value! as DraftSyncVersion;
        if (version.minVersion < minDraftVersion) {
            clearDownloading()
            return EmptyResponse.sendError(`fetch project failed: project: ${projectId} isn't supported on this device` )
        }

        if (version.isWaitingForData()) {
            this.delegate?.cloudManagerDidDetectMetaDataChange();
            clearDownloading()

            if (version.syncStatus === SyncStatus.Deleted) {
                return EmptyResponse.sendError(`sync: fetch project failed: project: ${projectId}, was deleted during fetching from device: ${device.id}, retrying` );
            } else if (version.syncStatus === SyncStatus.Uploading) {
                return EmptyResponse.sendError(`sync: fetch project failed: project: ${projectId}, was changed during fetching and still uploading to device: ${device.id}, retrying`);
            }
            return EmptyResponse.sendError(`fetch project failed: ${version} isn't synced`);
        }

        const draftResponse = await this.downloadProjectFiles(projectId, device, process, refetch, latestOnly);

        if (draftResponse.error) {
            clearDownloading()
            return EmptyResponse.sendError(draftResponse.error);
        }

        const updatedResponse = await this.fetchProjectUpdatedVersion(projectId, device);
        if (updatedResponse.error) {
            clearDownloading()
            return EmptyResponse.sendError(updatedResponse.error);
        }

        const updatedVersion = updatedResponse.value! as DraftSyncVersion;
        if (Math.abs(updatedVersion.statusUpdateDate.getTime() - version.statusUpdateDate.getTime()) > 100) {
            this.delegate?.cloudManagerDidDetectMetaDataChange();

            if (updatedVersion.syncStatus === SyncStatus.Deleted) {
                return EmptyResponse.sendError(`sync: fetch project failed: project: ${projectId}, was deleted during fetching from device: ${device.id}, retrying`);
            } else if (updatedVersion.syncStatus === SyncStatus.Uploading || updatedVersion.syncStatus === SyncStatus.WaitingForUpload) {
                return EmptyResponse.sendError(`sync: fetch project failed: project: ${projectId}, was changed during fetching and still uploading to device: ${device.id}, retrying`);
            } else if (updatedVersion.syncStatus !== SyncStatus.Updated) {
                console.warn(`sync: fetch project failed: project: ${projectId}, was changed during fetching from device: ${device.id}, state is: ${updatedVersion.syncStatus}`);
            }
            console.warn(`sync: fetch project failed: project: ${projectId}, was changed during fetching from device: ${device.id}, retrying`);
            
            let tempProject = (draftResponse as CloudResponse<CloudTempProject>).value

            clearDownloading()
            return this.fetchProject(projectId, device, tempProject);
        }

        clearDownloading()
        return draftResponse;
    }

    async deleteProject(projectId: string): Promise<EmptyResponse> {
        this.logWithTimestamp(`sync: deleting project: ${projectId}`);

        await this.cancelUploadingProject(projectId);
        await this.cancelFetchingProject(projectId);

        this.cachedProjectFilesList.delete(projectId);
        const filesReq = await this.allCloudProjectsFiles(projectId, UserDevice.current.id, false);

        if (!filesReq.succeed) {
            return filesReq as EmptyResponse;
        }

        try {
            await CloudUrl.deleteFile(CloudUrl.projectFilesList(projectId));
        } catch (error) {
            return EmptyResponse.sendError(`sync: failed to delete cloud project: ${projectId}, error: ${error}`);
        }

        let projectsFilesMetaData = (filesReq as CloudResponse<ProjectFileMetaData[]>).value
        for (const file of projectsFilesMetaData!) {
            try {
                await CloudUrl.deleteFile(CloudUrl.projectUrl(file.relativePath));
            } catch (error) {
                if (error.code === "storage/object-not-found") {
                    this.logWithTimestamp(`sync: file ${file.relativePath} was deleted already, and doesn't exist anymore on cloud`);
                } else {
                    EmptyResponse.sendError(`sync: failed to delete cloud project: ${projectId}, file: ${file.relativePath}, error: ${error}`);
                }
            }
        }
        return EmptyResponse.success();
    }

    async deleteProjects(projectIds: string[], projectCompletionId?: string) {
        for (const id of projectIds) {
            const res = await this.deleteProject(id);
            if (res.success && projectCompletionId) {
                this.delegate?.cloudManagerDidDeleteProject?.(id, projectCompletionId);
            }
        }
    }

    async uploadProject(project: Project, projectUrl: URL, didStartBlock?: (() => void) | string): Promise<EmptyResponse> {
        this.logWithTimestamp(`sync: cloud manager - upload project ${project.projectId}`);

        let that = this;
        return this.uploadList.runTask(() => new Promise(async function(resolve, reject) {
            const migrator = new BtDraftMigrator();
            const migrateResult = await migrator.asyncMigrate(project);
            if (!migrateResult.success) {
                resolve(EmptyResponse.sendError(`sync: failed to migrate project ${project.projectId} before uploading to cloud`));
                return;
            }

            const projectId = project.projectId!;
            const process = new CloudProcess(projectId, ProcessType.Upload);
            that.uploadingByDraftId.set(projectId, { process });

            await CloudUploadTempDataManager.shared.setStatus(projectId, SyncStatus.Uploading);

            const res = await that.uploadProjectAssets(project, projectUrl.toString(), process, didStartBlock);
            
            if (!res.succeed) {
                resolve(EmptyResponse.sendError(`sync: failed to upload project ${project.projectId}`));
                return;
            }

            await CloudUploadTempDataManager.shared.setStatus(projectId, SyncStatus.Updated);

            that.uploadingByDraftId.delete(projectId);
            resolve(EmptyResponse.success());
        })).then((result) => {
            return result as EmptyResponse;
        });
    }

    isDownloading(projectId: string): boolean {
        const action = this.fetchingByDraftId.get(projectId);
        if (!action) return false;
        if (action.progress && action.progress.isCanceled) return false;
        return true;
    }

    progressPercent(projectId: string): number {
        const tuple = this.fetchingByDraftId.get(projectId) ?? this.uploadingByDraftId.get(projectId);
        if (!tuple) return -1;
        if (!tuple.progress) return 0;
        return tuple.progress.percent;
    }

    async cleanDeviceCloudOldFiles(currentVersions: DraftSyncVersion[]) {
        try {
            const childs = await CloudUrl.listAll(CloudUrl.projectBaseUrl());

            const shouldDeleteFile = (projectId: string | null) => {
                if (!projectId) return false;
                const version = currentVersions.find(v => v.draftId === projectId);
                return !version || SyncStatus.isDeleted(version.syncStatus);
            };
            
            const projects = childs.prefixes.map(prefix => prefix as CloudUrl.ExtendedStorageReference).filter(url => shouldDeleteFile(CloudUrl.relativeToProjectsPath(url)));
            const projectsFilesList = childs.items.map(prefix => prefix as CloudUrl.ExtendedStorageReference).filter(url => shouldDeleteFile(CloudUrl.relativeToProjectsPath(url as CloudUrl.ExtendedStorageReference)));

            this.logWithTimestamp(`sync: cleaning old cloud files. ${projects.length} projects, and ${projectsFilesList.length} files lists`);

            for (const url of projects) {
                this.logWithTimestamp(`sync: clean old project files: ${CloudUrl.lastComponent(url) ?? "?"}`);
                await CloudUrl.deleteAllFiles(url as CloudUrl.ExtendedStorageReference);
            }

            for (const url of projectsFilesList) {
                this.logWithTimestamp(`sync: clean old project files metadata: ${CloudUrl.lastComponent(url) ?? "?"}`);
                await CloudUrl.deleteAllFiles(url);
            }
        } catch (error) {
            console.warn(`Sync: failed to clean project list of device: ${UserDevice.current}, error: ${error}`);
        }
    }

    ////////////////////////////
    ///////// private //////////
    ////////////////////////////
    //MARK: private

    private notifyUploadProjectDidStart(didStart?: (() => void) | string): void {
        if (!didStart) return;

        if (typeof didStart === "function") {
            didStart();
            return;
        }

        this.delegate?.cloudManagerDidStartUploadProject?.(didStart);
    }

    private async uploadProjectAssets(project: Project, projectUrl: string, process: CloudProcess, didStartBlock?: (() => void) | string): Promise<EmptyResponse | CloudResponse<ProjectFileMetaData[]>> {
        const projectId = project.projectId!;

        this.notifyUploadProjectDidStart(didStartBlock);

        // Do not rebuild index when missing: on first upload there is no _files.json yet
        const cloudUrlsRes = await this.allCloudProjectsFiles(projectId, UserDevice.current.id, false, false);
        // if (cloudUrlsRes.error) {
        //     return cloudUrlsRes;
        // }

        const cloudUrls = (cloudUrlsRes as CloudResponse<ProjectFileMetaData[]>).value?.filter ((f) => (!!f)) ?? [];
        const localUrls = await this.allLocalFiles(projectUrl, cloudUrls);

        try {
            await CloudUrl.deleteFile(CloudUrl.projectFilesList(projectId));
        } catch (error) {
            EmptyResponse.sendError(`sync: failed to delete files list of project before updating: ${projectId}, error: ${error}`);
        }

        const filesToSync = this.fileTransfer(cloudUrls, localUrls, ProcessType.Upload);
        const progress = new CloudProgress(filesToSync);

        this.uploadingByDraftId.set(projectId, { process, progress });

        const uploading = filesToSync.filter(file => !file.shouldDelete);
        const linking = uploading.filter(file => file.isHardLink);
        const size = uploading.reduce((total, file) => total + (file.size || 0), 0);

        this.logWithTimestamp(`sync: deleting ${filesToSync.length - uploading.length} files, uploading ${uploading.length - linking.length} files, linking: ${linking.length} files, size: ${size}`);

        const res = await process.runFileTransfers(filesToSync, progress);
        if (res.error) {
            return res;
        }

        try {
            // this.cachedProjectFilesList.set(projectId, localUrls);
            await CloudUrl.putJson(CloudUrl.projectFilesList(projectId), localUrls);
        } catch (error) {
            return EmptyResponse.sendError(`sync: failed to upload files list of project: ${projectId}, error: ${error}`);
        }

        return EmptyResponse.success();
    }

    /**
     * Fetches the project files index (_files.json) from cloud.
     * @param rebuildIndexIfMissing When true (default), if the index is missing we rebuild it from storage (recovery).
     * When false (e.g. upload path), missing index is treated as "no cloud files yet" and we return empty list —
     * do not rebuild on first initial upload when the file has never existed.
     */
    private async allCloudProjectsFiles(projectId: string, ofDeviceId: string, shouldCache: boolean, rebuildIndexIfMissing: boolean = true): Promise<CloudResponse<ProjectFileMetaData[]> | EmptyResponse> {
        try {
            // if (this.cachedProjectFilesList.get(projectId)) {
            //     return new SuccesfulResponse(this.cachedProjectFilesList.get(projectId));
            // }
            
            const filesList = CloudUrl.projectFilesList(projectId, ofDeviceId);
            const data = await CloudUrl.get(filesList);
            let decoder = new SyncDecoder<ProjectFileMetaData>();
            const files = decoder.convertArray(data);

            // if (shouldCache) {
            //     this.cachedProjectFilesList.set(projectId, files);
            // }
            return new SuccesfulResponse(files);
        } catch {
            if (!rebuildIndexIfMissing) {
                // e.g. first initial upload: index was never created; treat as no cloud files
                return new SuccesfulResponse([]);
            }
            this.logWithTimestamp(`sync: project files index missing, rebuilding from storage`, projectId, ofDeviceId);
            Sentry.captureMessage(`sync: project files index (_files.json) missing, rebuilding from storage`, {
                level: "warning",
                extra: { projectId, deviceId: ofDeviceId },
            });

            try {
                const storage = FirebaseWrapper.FirebaseBaseWrapper.shared.storage;
                const projectRootPath = CloudUrl.projectUrl(projectId, ofDeviceId).fullPath;
                const entries = await rebuildProjectFilesIndex({
                    storage,
                    projectId,
                    projectRootPath,
                    sizeInKb: false,
                    layersDirName: "Layers",
                    crawlEntireProjectTree: true,
                    uploadIndex: false,
                });
                const files: ProjectFileMetaData[] = entries.map((entry) => {
                    const raw = entry["bazaart.ProjectFileMetaData"];
                    const meta = ProjectFileMetaData.fromJSON(raw);
                    meta.fileNumber = raw.fileNumber;
                    // RebuildFilesIndex already returns projects-relative path (projectId/...) when projectRootPath is set
                    return meta;
                });
                return new SuccesfulResponse(files);
            } catch (rebuildError) {
                return EmptyResponse.sendError(`sync: failed to rebuild project files list from storage: ${projectId}, error: ${rebuildError}`);
            }
        }
    }

    private async allLocalFiles(url: string, cloudFiles: ProjectFileMetaData[]): Promise<ProjectFileMetaData[]> {
        // Metadata-only, files-only listing — never deserializes blob bytes. An empty
        // result also covers the "nothing exists" case, so no separate exists() scan.
        const records = await BtDiskFileUtils.listMetadata(url);
        const res = ProjectFileMetaData.fileLinksMetaDataOutOfLocalUrls(records);
        const count = res.reduce((total, links) => total + links.length, 0);

        if (count !== records.length) {
            console.warn("sync: local files links count doesn't match the given urls");
            return [];
        }

        const cloudFilesByPath = new Map(cloudFiles.map(file => [file.relativePath, file]));

        for (const links of res) {
            if (links.length > 1) {
                const existOnCloud = links.find(link => cloudFilesByPath.has(link.relativePath) && !cloudFilesByPath.get(link.relativePath)!.isHardLink);
                const sourceFile = existOnCloud || links[0];
                const hardLinks = links.filter(link => link !== sourceFile);
                for (const link of hardLinks) {
                    link.linkTo(sourceFile);
                }
            }
        }

        return res.flat();
    }

    private fileTransfer(cloudUrls: ProjectFileMetaData[], localUrls: ProjectFileMetaData[], processType: ProcessType, deviceId: string | null = null): CloudProjectFileTransfer[] {
        const sources = processType === ProcessType.Upload ? localUrls : cloudUrls;
        const targets = processType === ProcessType.Upload ? cloudUrls : localUrls;

        const targetDict = new Map(targets.map(file => [file.relativePath, file]));

        const missingFiles: CloudProjectFileTransfer[] = [];
        const needToUpdateFiles: CloudProjectFileTransfer[] = [];
        const needToDelete: CloudProjectFileTransfer[] = [];

        for (const source of sources) {
            const relativePath = source.relativePath;
            const file = new CloudProjectFileTransfer(source, processType, source.modifiedDate, deviceId);

            const targetFile = targetDict.get(relativePath);
            if (targetFile) {
                if (targetFile.isHardLink !== source.isHardLink) {
                    this.logWithTimestamp(`sync - file link attribute was changed somehow: ${relativePath}`);
                    if (source.isHardLink) {
                        file.shouldDelete = true;
                        needToDelete.push(file);
                    } else {
                        missingFiles.push(file);
                    }
                } else if (targetFile.timeStamp !== source.timeStamp) {
                    needToUpdateFiles.push(file);
                }
            } else {
                missingFiles.push(file);
            }
            targetDict.delete(relativePath);
        }

        for (const targetFile of targetDict.values()) {
            needToDelete.push(new CloudProjectFileTransfer(targetFile, processType, null, deviceId, true));
        }

        return [...missingFiles, ...needToUpdateFiles, ...needToDelete];
    }

    // private lastPathComponent(url: URL): string {
    //     return url.href.split("/").pop();
    // }

    // private async updateFonts() {
    //     const cloudFontsRes = await this.downloadFontsFileNames();
    //     if (cloudFontsRes.error) {
    //         console.warn("sync: failed to get cloud font list, can't update custom fonts");
    //         return;
    //     }

    //     const cloudFonts = (cloudFontsRes as CloudResponse<string[]>).value;
    //     const localFonts = FontsManager.shared.getFonts(font => font.isCustomFont).map(font => this.lastPathComponent(font.fileURL));

    //     const dictLocalFonts = new Set(localFonts);
    //     const dictCloudFonts = new Set(cloudFonts);

    //     const uploadFonts = localFonts.filter(font => !dictCloudFonts.has(font));
    //     const downloadFonts = cloudFonts.filter(font => !dictLocalFonts.has(font) && !FontsManager.shared.isHiddenCustomFont(font));

    //     this.logWithTimestamp(`sync: updating custom fonts: uploading ${uploadFonts.length} fonts, downloading ${downloadFonts.length} fonts.`);

    //     for (const font of uploadFonts) {
    //         await this.uploadFont(font, false);
    //     }

    //     for (const font of downloadFonts) {
    //         await this.fetchFont(font);
    //     }

    //     if (downloadFonts.length > 0) {
    //         FontsManager.shared.fetchLocalCustomFonts();
    //     }
    // }


    // async fetchFont(fileName: string): Promise<EmptyResponse> {
    //     const transfer = new CloudFontFileTransfer(fileName, ProcessType.Download, undefined);
    //     const fontCloudUrl = await CloudUrl.getDownloadUrl(transfer.cloudUrl);

    //     let font = {
    //         name: fileName,
    //         url: fontCloudUrl,
    //         options: { weight: '400' },
    //     }

    //     try {
    //         await MediaFontRepository.getInstance().loadFontFromUrl(font.name, font.url, font.options)
    //         return EmptyResponse.success()
    //     } catch (err) {
    //         return EmptyResponse.sendError(err)
    //     }
    // }

    // async uploadFont(fileName: string, shouldCheckIfExist = true): Promise<EmptyResponse> {
    //     this.logWithTimestamp(`sync: uploading font: ${fileName}`);
    //     if (shouldCheckIfExist) {
    //         const fontsRes = await this.downloadFontsFileNames();
    //         if (fontsRes.error) {
    //             return fontsRes as EmptyResponse;
    //         }

    //         let fonts = (fontsRes as CloudResponse<string[]>).value
    //         if (fonts.includes(fileName)) {
    //             this.logWithTimestamp(`sync: uploading font: ${fileName} - font already exists`);
    //             return EmptyResponse.success();
    //         }
    //     }

    //     const res = await new CloudFontFileTransfer(fileName, ProcessType.Upload, undefined).run(2, null);
    //     if (res.success) {
    //         this.notifyStatusChange();
    //     }
    //     return res;
    // }

    private async downloadProjectFiles(projectId: string, device: UserDevice, process: CloudProcess, reDownload?: CloudTempProject, latestOnly: boolean = false): Promise<CloudResponse<CloudTempProject> | EmptyResponse> {
        const tempProject = reDownload || new CloudTempProject(projectId);
        
        if (!tempProject.loadProjectConfig()) {
            return EmptyResponse.sendError(`sync: failed to create temp project ${projectId}`);
        }

        const cloudUrlsRes = await this.allCloudProjectsFiles(projectId, device.id, !latestOnly);
        if (cloudUrlsRes.error) {
            return cloudUrlsRes as EmptyResponse;
        }

        const cloudUrls = (cloudUrlsRes as CloudResponse<ProjectFileMetaData[]>).value!;
        const localUrls = await this.allLocalFiles(tempProject.getProjectUrl().pathname, cloudUrls);

        let filesToSync = this.fileTransfer(cloudUrls, localUrls, ProcessType.Download, device.id);
        if (latestOnly) {
            filesToSync = filesToSync.filter ((f) => f.cloudUrl.fullPath.toLowerCase().indexOf('latest') !== -1 || f.cloudUrl.fullPath.toLowerCase().indexOf('/layers/') == -1);
        }
        const progress = new CloudProgress(filesToSync);
        this.fetchingByDraftId.set(projectId, { process, progress });

        const res = await process.runFileTransfers(filesToSync, progress);
        if (res.error) {
            return res as EmptyResponse;
        }
        if (!tempProject.loadProjectConfig()) {
            return EmptyResponse.sendError(`sync: failed to load draft from temp folder: ${projectId}`);
        }
        return new SuccesfulResponse(tempProject);
    }
    
    private async fetchProjectUpdatedVersion(projectId: string, device: UserDevice): Promise<CloudResponse<DraftSyncVersion> | EmptyResponse> {
        const res = await this.downloadMetaData(device.id);
        if (res.error) { 
            return EmptyResponse.sendError(res.error);
        }
        let draftSyncVersionResponse = res as CloudResponse<DeviceCloudMetadata>;
        const projectMetaData = draftSyncVersionResponse.value.draftSyncVersions.find(version => version.draftId === projectId);
        if (!projectMetaData) {
            this.delegate?.cloudManagerDidDetectMetaDataChange();
            return {
                error: `sync: missing project: ${projectId} from device metaData: ${device.id}`,
                succeed: false 
             };
        }

        return { 
            value: projectMetaData,
            succeed: true
        };
    }
    
    private async downloadMetaData(deviceId: string): Promise<CloudResponse<DeviceCloudMetadata> | EmptyResponse> {
        const data = await CloudUrl.get(CloudUrl.deviceMetaData(deviceId));
        try {
            let decoder = new SyncDecoder<DeviceCloudMetadata>();
            const metadata = decoder.convertSingle(data);
            return new SuccesfulResponse(metadata);
        } catch (error) {
            let decoder = new SyncDecoder<DeviceCloudMetadata>();
            const metadata = decoder.convertSingle(data);
            return EmptyResponse.sendError(`sync: couldn't fetch metadata of device: ${deviceId}. error: ${error}`);
        }
    }

    private async downloadDeviceIds(): Promise<CloudResponse<string[]> | EmptyResponse> {
        try {
            const res = await CloudUrl.listAll(CloudUrl.devicesBaseUrl());
            let webOnlyDevice = res.prefixes.map(prefix => prefix.name).filter( r => r.toLowerCase() == 'web');
            return new SuccesfulResponse(webOnlyDevice);
        } catch (error) {
            return EmptyResponse.sendError(`sync: failed to receive cloud devices. error: ${error}`);
        }
    }

    async didHaveSync(): Promise<CloudResponse<boolean> | EmptyResponse> {
        const req = await this.downloadDeviceIds();
        if (req.error) return req as EmptyResponse;
        let values = req.value as string[]
        return new SuccesfulResponse(values.length > 0);
    }

    async downloadFontsFileNames(): Promise<CloudResponse<string[]> | EmptyResponse> {
        try {
            const res = await CloudUrl.listAll(CloudUrl.fontsBaseUrl());
            return new SuccesfulResponse(res.items.map(item => item.name));
        } catch (error) {
            return EmptyResponse.sendError(`sync: failed to receive cloud fonts list. error: ${error}`);
        }
    }

    logWithTimestamp(...messages: any[]): void {
        if(process.env.NODE_ENV !== 'production') {
            const timestamp = new Date().toISOString();
            console.info(`[${timestamp}]`, ...messages);
        }
    }
}
